import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getRestaurants } from '../../services/businessService';

interface RestaurantOption {
  _id: string;
  name: string;
  slug: string;
}

interface Props {
  label?: string;
  value: string;
  onChange: (slug: string) => void;
  required?: boolean;
  helperText?: string;
  error?: boolean;
  errorText?: string;
}

export default function RestaurantSlugSelect({
  label = 'Restaurant',
  value,
  onChange,
  required,
  helperText,
  error,
  errorText,
}: Props) {
  const { data: restaurants = [], isLoading } = useQuery({
    queryKey: ['restaurants-admin-select'],
    queryFn: () => getRestaurants(true),
  });

  const options: RestaurantOption[] = useMemo(
    () => (restaurants as Array<{ _id: string; name: string; slug: string }>).map((r) => ({
      _id: r._id,
      name: r.name,
      slug: r.slug,
    })),
    [restaurants],
  );

  const selected = useMemo(
    () => options.find((o) => o.slug === value) || null,
    [options, value],
  );

  return (
    <Autocomplete
      size="small"
      disabled={isLoading}
      options={options}
      value={selected}
      getOptionLabel={(o) => `${o.name} (${o.slug})`}
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      onChange={(_, opt) => onChange(opt?.slug || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || !!errorText}
          helperText={errorText || helperText || 'Select restaurant from catalog'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={isLoading ? 'Loading…' : 'No restaurants — add restaurants first'}
    />
  );
}
